/* KB owns cart items, gifts, discounts and selling plans. This element only
   presents its current selection and activates its native purchase control. */
(() => {
  if (customElements.get('vv-buy-bottom')) return;
  class VVBuyBottom extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.scope = this.closest('.shopify-section');
      if (!this.scope) return;
      this.config = JSON.parse(this.querySelector('[data-buy-bottom-config]').textContent);
      this.toggle = this.querySelector('[data-purchase-toggle]');
      this.benefits = this.querySelector('[data-subscription-benefits]');
      this.note = this.querySelector('[data-purchase-note]');
      this.error = this.querySelector('[data-purchase-error]');
      this.schedule = () => { clearTimeout(this.timer); this.timer = setTimeout(() => this.sync(), 0); };
      this.scope.addEventListener('change', this.schedule, { signal });
      this.scope.addEventListener('input', this.schedule, { signal });
      this.scope.addEventListener('items-changed', this.schedule, { signal });
      this.scope.addEventListener('kaching-bundles-block-loaded', this.schedule, { signal });
      window.addEventListener('kaching-bundles-initialized', this.schedule, { signal });
      this.toggle.addEventListener('click', () => this.togglePurchase(), { signal });
      this.addEventListener('click', event => {
        if (event.target.closest('button[name="add"]')) this.guard(event);
      }, { capture: true, signal });
      this.addEventListener('submit', event => this.guard(event), { capture: true, signal });
      this.observer = new MutationObserver(records => {
        // Ignore our own label/classes to avoid a render-observe loop.
        if (records.some(({ target }) => target !== this.scope && (
          !this.contains(target) || target.closest?.('kaching-subscriptions, kaching-bundles-block') ||
          target.matches?.('form, input, select')
        ))) this.schedule();
      });
      this.observer.observe(this.scope, { childList: true, subtree: true, attributes: true,
        attributeFilter: ['aria-checked', 'data-selling-plan-id', 'selling-plan-id', 'current-variant-id', 'class', 'value', 'quantity'] });
      this.sync();
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.observer?.disconnect(); clearTimeout(this.timer);
      this.scope?.classList.remove('vv-buy-bottom-ready', 'vv-buy-bottom-standalone-ready');
    }
    getBundle() {
      return this.scope.querySelector(`kaching-bundle[product-id="${this.dataset.productId}"] kaching-bundles-block`);
    }
    getStandalone() {
      return this.scope.querySelector(`kaching-subscriptions[product-id="${this.dataset.productId}"] kaching-subscriptions-block-v2`);
    }
    getControl() {
      const bundle = this.getBundle();
      const bundleControl = bundle?.querySelector('.kaching-bundles__subscriptions--checkbox [role="checkbox"]');
      if (bundleControl) return bundleControl;
      const widget = this.getStandalone();
      const cards = widget?.querySelectorAll('.kaching-subscriptions__card-wrapper[role="button"]');
      // Only replace a single native checkbox. Other layouts keep their own UI.
      if (cards?.length === 1 && cards[0].querySelector('.kaching-subscriptions__card-checkbox')) return cards[0];
      return null;
    }
    getItems() {
      const bundle = this.getBundle();
      if (!bundle) {
        const widget = this.getStandalone();
        if (!widget || !this.getControl()) return null;
        const form = this.querySelector('form');
        const id = form?.querySelector('[name="id"]')?.value;
        if (!id) return null;
        const quantity = this.scope.querySelector(`[name="quantity"][form="${form.id}"]`)?.value
          || form.querySelector('[name="quantity"]')?.value || widget.getAttribute('quantity') || 1;
        const sellingPlan = form.querySelector('[name="selling_plan"]')?.value;
        // Read only: the plugin remains the sole owner of selling-plan inputs.
        return [{ id, quantity: Number(quantity), selling_plan: sellingPlan || null }];
      }
      if (typeof bundle.items !== 'function') return null;
      try {
        const items = bundle.items();
        if (!Array.isArray(items) || !items.length) return null;
        // When Bundles only owns quantities/discounts, the standalone widget
        // owns the purchase mode. Read its form input for display without
        // mutating Bundles items or taking over either app's cart submission.
        const bundleControl = bundle.querySelector?.('.kaching-bundles__subscriptions--checkbox [role="checkbox"]');
        if (!bundleControl && this.getStandalone() && this.getControl()) {
          const sellingPlan = this.querySelector('form')?.querySelector('[name="selling_plan"]')?.value;
          return items.map(item => {
            if (!this.config.variants.some(variant => String(variant.id) === String(item.id))) return item;
            try { if (JSON.parse(item.properties?.__kaching_bundles || '{}').gift) return item; }
            catch { /* Ordinary line properties need no special handling. */ }
            return { ...item, selling_plan: sellingPlan || null };
          });
        }
        return items;
      } catch { return null; }
    }
    guard(event) {
      if (this.getItems()) { this.error.hidden = true; return; }
      event.preventDefault(); event.stopImmediatePropagation();
      this.error.textContent = 'Your bundle is still loading. Please try again in a moment.';
      this.error.hidden = false;
    }
    togglePurchase() {
      const control = this.getControl();
      if (!control || control.getAttribute('aria-disabled') === 'true') {
        this.error.textContent = 'Purchase options are loading. Please try again.';
        this.error.hidden = false; return;
      }
      this.subscriptionDefaultApplied = true;
      control.click();
      this.schedule();
    }
    initializeSubscription(items, control) {
      if (this.subscriptionDefaultApplied || !items || !control || control.getAttribute('aria-disabled') === 'true') return false;
      // Apply once per instance. Never override a later customer purchase choice.
      this.subscriptionDefaultApplied = true;
      const bundleControl = this.getBundle()?.querySelector('.kaching-bundles__subscriptions--checkbox [role="checkbox"]');
      const standalone = this.getStandalone();
      // The native widget may already show subscription before its form input updates.
      const nativeSubscriptionSelected = !bundleControl && standalone && !standalone.querySelector('.one-time-purchase-selected');
      if (items.some(item => item.selling_plan) || control.getAttribute('aria-checked') === 'true' || nativeSubscriptionSelected) return false;
      control.click();
      this.schedule();
      return true;
    }
    setText(element, text) { if (element.textContent !== text) element.textContent = text; }
    sync() {
      const bundle = this.getBundle(), items = this.getItems();
      const control = this.getControl();
      if (this.initializeSubscription(items, control)) return;
      this.toggle.disabled = !items || !control || control.getAttribute('aria-disabled') === 'true';
      this.scope.classList.toggle('vv-buy-bottom-ready', !!bundle && !this.toggle.disabled);
      this.scope.classList.toggle('vv-buy-bottom-standalone-ready', !bundle && !this.toggle.disabled);
      this.querySelector('.vv-buy-bottom__purchase').hidden = !bundle && !this.config.plans.length;
      this.error.hidden = true;
      if (!items) return;
      const recurring = items.filter(item => item.selling_plan);
      const subscribed = recurring.length > 0;
      this.benefits.hidden = !subscribed;
      this.setText(this.toggle, subscribed ? 'BUY ONCE - NO SAVINGS →' : 'SUBSCRIBE & SAVE →');
      this.toggle.setAttribute('aria-label', subscribed ? 'Switch to one-time purchase' : 'Switch to subscription');
      this.setText(this.note, subscribed
        ? 'Subscribe for convenient automatic deliveries.'
        : 'One-time purchase selected. No recurring deliveries or charges.');
      if (!subscribed) return;
      const refillElement = this.querySelector('[data-refill-copy]');
      const customRefillText = (refillElement.dataset.refillCustom || '').trim();
      if (customRefillText) {
        this.setText(refillElement, customRefillText);
        return;
      }
      const paid = recurring.filter(item => {
        try { return !JSON.parse(item.properties?.__kaching_bundles || '{}').gift; }
        catch { return true; }
      });
      let quantity = 0, price = null, period = null;
      for (const item of paid) {
        const variant = this.config.variants.find(v => String(v.id) === String(item.id));
        const allocation = variant?.allocations.find(a => String(a.id) === String(item.selling_plan));
        const plan = this.config.plans.find(p => String(p.id) === String(item.selling_plan));
        if (!allocation || !plan) { price = null; break; }
        const option = plan.options.find(o => o.name === 'config');
        let delivery;
        try { delivery = JSON.parse(option?.value || '{}').delivery; } catch { /* leave generic copy */ }
        if (!delivery?.interval || !delivery.frequency) { price = null; break; }
        const unit = delivery.interval.toLowerCase();
        const currentPeriod = `every ${delivery.frequency === 1 ? '' : delivery.frequency + ' '}${unit}${delivery.frequency === 1 ? '' : 's'}`;
        if (price !== null && (price !== allocation.price || period !== currentPeriod)) { price = null; break; }
        price = allocation.price; period = currentPeriod; quantity += Number(item.quantity);
      }
      let copy = 'Automatic deliveries with your selected subscription';
      if (price !== null && quantity > 0) {
        const money = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency', currency: this.config.currency, minimumFractionDigits: price % 100 ? 2 : 0
        }).format(price / 100);
        copy = `${quantity} ${quantity === 1 ? 'item' : 'items'} at ${money} each, delivered ${period}`;
      }
      this.setText(refillElement, copy);
    }
  }
  customElements.define('vv-buy-bottom', VVBuyBottom);
})();
